/**
 * @license Siftrock
 * (c) 2014-2018 Siftrock, Inc. http://siftrock.com
 * Unauthorized copying of this file, via any medium is strictly prohibited
 */

import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { NgModule, ErrorHandler, APP_INITIALIZER } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpModule } from '@angular/http'; // still needed for some legacy features and socket-io
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { RouterModule } from '@angular/router';

import { SelectModule } from 'ng-select';
import { MomentModule } from 'angular2-moment';

import { PrettyJsonModule } from 'angular2-prettyjson';

import { AppComponent } from './app.component';

import { Constants } from './global/constants';

import { EditorModule } from '@tinymce/tinymce-angular';
import { BsDropdownModule, ModalModule, PopoverModule, AccordionModule, TooltipModule } from 'ngx-bootstrap';
import { CookieService } from 'ngx-cookie-service';

// some Highcharts shenanigans -
// https://github.com/gevgeny/angular2-highcharts/issues/156
import { HighchartsStatic } from 'angular2-highcharts/dist/HighchartsService';
import { ChartModule } from 'angular2-highcharts';
export function highchartsFactory() {
  return require('highcharts');
}

import { CacheService } from './global/ng2-cache/services/cache.service';
import { JwtHelper } from 'angular2-jwt';
import { ClipboardModule } from 'ngx-clipboard';

import { MultiSelectModule } from './global/multi-select-dropdown/multiselect.component';

import { GlobalErrorHandler } from './global/error-handler';

import { AuthInterceptor } from './global/auth-interceptor';
import { AuthService } from './services/auth.service';
import { ErrorService } from './services/error.service';
import { InitService } from './services/init.service';
import { LogService } from './services/log.service';
import { SentryService } from './services/sentry.service';
import { SessionService } from './services/session2.service';
import { ConfigService } from './services/config.service';
import { GlobalMessageService } from './services/global-message.service';
import { AccountService } from './services/account.service';
import { SkuService } from './services/sku.service';
import { UserService } from './services/user.service';
import { MarketoService } from './services/marketo.service';
import { EloquaService } from './services/eloqua.service';
import { HubspotService } from './services/hubspot.service';
import { PardotService } from './services/pardot.service';
import { ActonService } from './services/acton.service';
import { PartnerService } from './services/partner.service';
import { AggregateService } from './services/aggregate.service';
import { ContactService } from './services/contact.service';
import { ContactIgnoreService } from './services/contact-ignore.service';
import { EmailDetailService } from './services/email-detail.service';
import { WorkflowService } from './services/workflow.service';
import { EnrichService } from './services/enrich.service';
import { AutomateJobService } from './services/automate-job.service';
import { AutomateTaskHistoryService } from './services/automate-task-history.service';
import { ApproveQService } from './services/approve-q.service';
import { SourceService } from './services/source.service';
import { DriftService } from './services/drift.service';
import { UtilService } from './services/util.service';
import { EventService } from './services/event.service';
import { GmailService } from './services/gmail.service';
import { O365Service } from './services/o365.service';
import { NerService } from './services/ner.service';
import { MatchesService } from './services/matches.service';
import { FilterService } from './services/filter.service';
import { ParserService } from './services/parser.service';
import { OpsService } from './services/ops.service';
import { AdminService } from './services/admin.service';
import { ClassifierService } from './services/classifier.service';
import { ChartService } from './services/chart.service';
import { VersionService } from './services/version.service';
import { AdminHeaderClassifierService } from './services/admin/admin-headerclassifier.service';
import { AdminNBClassifierService } from './services/admin/admin-nbclassifier.service';
import { AdminRegexClassifierService } from './services/admin/admin-regexclassifier.service';
import { AdminSpamClassifierService } from './services/admin/admin-spamclassifier.service';
import { AdminOpsService } from './services/admin/admin-ops.service';
import { AdminOpsInternalService } from './services/admin/admin-ops-internal.service';
import { AdminSubjectClassifierService } from './services/admin/admin-subjectclassifier.service';
import { AdminClassifierService } from './services/admin/admin-classifier.service';
import { AdminCacheService } from './services/admin/admin-cache.service';
import { AdminGoldensetResultService } from './services/admin/admin-goldenset-result.service';
import {ShortUrlService} from './services/shorturl.service';

import { HomeComponent } from './home/home.component';
import { DashboardComponent } from './dashboard/dashboard.component';
import { SuspendedComponent } from './suspended/suspended.component';
import { LoginComponent } from './login/login.component';
import { RegisterComponent } from './register/register.component';
import { SettingsComponent } from './settings/settings.component';

import { AuthGuard } from './auth.guard';
import { Animations } from './global/animations';

import { routerConfig } from './routes/root.routes';

import { AuthLevelDirective, AuthLevelMaskDirective } from './directives/auth-level.directive';

import { LogoutComponent } from './logout/logout.component';
import { LoadingComponent } from './loading/loading.component';
import { DriftNavComponent } from './drift-nav/drift-nav.component';
import { SettingsNavComponent } from './settings/settings-nav/settings-nav.component';
import { SettingsNavDropdownComponent } from './settings/settings-nav/settings-nav.component';
import { GlobalMessageComponent } from './global/global-message/global-message.component';
import { SettingsDomainsComponent } from './settings/settings-domains/settings-domains.component';
import { GlobalErrorComponent } from './global/global-error/global-error.component';
import { ConfirmDialogComponent } from './global/confirm-dialog/confirm-dialog.component';
import { SettingsIntegrationComponent } from './settings/settings-integration/settings-integration.component';
import { AlertDialogComponent } from './global/alert-dialog/alert-dialog.component';
import { SettingsContactComponent } from './settings/settings-contact/settings-contact.component';
import { SettingsUsersComponent } from './settings/settings-users/settings-users.component';
import { AccountDialogComponent } from './account-dialog/account-dialog.component';
import { ContactsComponent } from './contacts/contacts.component';
import { FilterPipe } from './global/pipes/filter.pipe';
import { CsvExportComponent } from './csv-export/csv-export.component';
import { InboxComponentOld } from './inbox-old/inbox.component';
import { EmailDetailComponentOLD } from './email-detail/email-detail.component';
import { WorkflowsComponent } from './config/workflows/workflows.component';
import { WfDescriptionComponent } from './config/workflows/wf-description.component';
import { WFDetailComponent } from './config/workflows/wf-detail.component';
import { WFActonDetailComponent } from './config/workflows/wf-acton-detail.component';
import { AlertWfDetailComponent } from './config/workflows/alert-wf-detail.component';
import { SettingsSubscriptionComponent } from './settings/settings-subscription/settings-subscription.component';
import { PasswordResetComponent } from './password-reset/password-reset.component';
import { DemoComponent } from './demo/demo.component';
import { PreTrialComponent } from './pre-trial/pre-trial.component';
import { AdminComponent } from './admin/admin.component';
import { AdminNavComponent } from './admin/admin-nav/admin-nav.component';
import { AdminAccountComponent } from './admin/admin-account/admin-account.component';
import { WorkflowHistoryComponent } from './config/workflows/workflow-history/workflow-history.component';
import { AdminWorkflowComponent } from './admin/admin-workflow/admin-workflow.component';
import { AdminEnrichComponent } from './admin/admin-enrich/admin-enrich.component';
import { SettingsInboxesComponent } from './settings/settings-inboxes/settings-inboxes.component';
import { InboxesAuthComponent } from './settings/settings-inboxes/inboxes-auth/inboxes-auth.component';
import { GoogleLoginComponent } from './login/google-login/google-login.component';
import { AdminNbClassifierComponent } from './admin/admin-nb-classifier/admin-nb-classifier.component';
import { NbClassifyOutputComponent } from './admin/classify-output/nb-classify-output.component';
import { AdminEmailDetailComponent } from './admin/admin-email-detail/admin-email-detail.component';
import { AdminNerComponent } from './admin/admin-ner/admin-ner.component';
import { NerOutputComponent } from './admin/ner-output/ner-output.component';
import { AdminParserComponent } from './admin/admin-parser/admin-parser.component';
import { AdminFilterComponent } from './admin/admin-filter/admin-filter.component';
import { AdminSubjectClassifierComponent } from './admin/admin-subject-classifier/admin-subject-classifier.component';
import { AdminSystemComponent } from './admin/admin-system/admin-system.component';
import { WfDetailPardotComponent } from './config/workflows/wf-detail-pardot/wf-detail-pardot.component';
import { AdminUtilitiesComponent } from './admin/admin-utilities/admin-utilities.component';
import { EnrichComponent } from './config/enrich/enrich.component';
import { EnrichGraphComponent } from './global/analytics/enrich-graph/enrich-graph.component';
import { AdminSpamClassifierComponent } from './admin/admin-spam-classifier/admin-spam-classifier.component';
import { SpamBodyOutputComponent } from './admin/classify-output/spam-body-output.component';
import { ConfigComponent } from './config/config.component';
import { ConfigNavComponent } from './config/config-nav/config-nav.component';
import { HumanGraphComponent } from './global/analytics/human-graph/human-graph.component';
import { DateRangePickerComponent } from './global/date-range/date-range-picker.component';
import { AdminCleanupComponent } from './admin/admin-cleanup/admin-cleanup.component';
import { AdminUnsubClassifierComponent } from './admin/admin-unsub-classifier/admin-unsub-classifier.component';
import { AdminHeaderClassifierComponent } from './admin/admin-header-classifier/admin-header-classifier.component';
import { CdoComponent } from './config/cdo/cdo.component';
import { CdoDetailComponent } from './config/cdo/cdo-detail.component';
import { EloquaCdoService } from './services/eloqua-cdo.service';
import { CdoDescriptionComponent } from './config/cdo/cdo-description.component';
import { EmailTypePickerComponent } from './global/email-type-picker/email-type-picker.component';
import { RouteTrackComponent } from './config/route-track/route-track.component';
import { RouteTrackDetailComponent } from './config/route-track/route-track-detail.component';
import { SourcePickerComponent } from './global/source-picker/source-picker.component';
import { LanguagePickerComponent } from './global/language-picker/language-picker.component';
import { AdminJobComponent } from './admin/admin-job/admin-job.component';
import { PartnerPickerComponent } from './global/partner-picker/partner-picker.component';
import { WizardStepsComponent } from './global/wizard-steps/wizard-steps.component';
import { PartnerMapComponent } from './global/partner-map/partner-map.component';
import { PartnerListPickerComponent } from './global/partner-list-picker/partner-list-picker.component';
import { PartnerStaticfieldPickerComponent } from './global/partner-staticfield-picker/partner-staticfield-picker.component';
import { PartnerSiftrockfieldPickerComponent } from './global/partner-siftrockfield-picker/partner-siftrockfield-picker.component';
import { PartnerLeadownerPickerComponent } from './global/partner-leadowner-picker/partner-leadowner-picker.component';
import { FieldFilterComponent } from './global/field-filter/field-filter.component';
import { AdminFailedTasksComponent } from './admin/admin-failed-tasks/admin-failed-tasks.component';
import { SourceFilterComponent } from './global/source-filter/source-filter.component';
import { GlobalUpdateComponent } from './global/global-update/global-update.component';
import { AdminJobsComponent } from './admin/admin-jobs/admin-jobs.component';
import { NewUserComponent } from './new-user/new-user.component';
import { AdminTypeOverrrideComponent } from './admin/admin-type-overrride/admin-type-overrride.component';
import { TypeOverrideService } from './services/type-override.service';
import { EmailFilterGroupComponent } from './global/email-filter-group/email-filter-group.component';
import { SettingsBotComponent } from './settings/settings-bot/settings-bot.component';
import { BotAuthComponent } from './settings/settings-bot/bot-auth/bot-auth.component';
import { AutomateComponent } from './automate/automate.component';
import { SkillListComponent } from './automate/skill-list/skill-list.component';
import { SkillService } from './services/skill.service';
import { AdminClassifierComponent } from './admin/admin-classifier/admin-classifier.component';
import { SkillDetailComponent } from './automate/skill-detail/skill-detail.component';
import { RouteTaskComponent } from './automate/tasks/route-task/route-task.component';
import { TaskDirective } from './automate/task.directive';
import { RespondTaskComponent } from './automate/tasks/respond-task/respond-task.component';
import { RespondOtterTaskComponent } from './automate/tasks/respondotter-task/respondotter-task.component';
import { TrackTaskComponent } from './automate/tasks/track-task/track-task.component';
import { RespondtrackTaskComponent } from './automate/tasks/respondtrack-task/respondtrack-task.component';
import { CreateTaskComponent } from './automate/tasks/create-task/create-task.component';
import { ConfidencePickerComponent } from './global/confidence-picker/confidence-picker.component';
import { InboxComponent } from './inbox/inbox.component';
import { EmailDetailComponent } from './inbox/email-detail/email-detail.component';
import { TaskHistoryComponent } from './inbox/task-history/task-history.component';
import { PartnerSenderfieldPickerComponent } from './global/partner-senderfield-picker/partner-senderfield-picker.component';
import { TaskPopoverComponent } from './automate/task-popover/task-popover.component';
import { FilterPopoverComponent } from './automate/filter-popover/filter-popover.component';
import { SettingsPrivacyComponent } from './settings/settings-privacy/settings-privacy.component';
import { BotDraftComponent } from './inbox/bot-draft/bot-draft.component';
import { AdminInboxComponent } from './admin/admin-inbox/admin-inbox.component';
import { AdminCacheComponent } from './admin/admin-cache/admin-cache.component';
import { AdminGoldensetComponent } from './admin/admin-goldenset/admin-goldenset.component';
import { GotoComponent } from './goto/goto.component';
import { AdminWebPushComponent } from './admin/admin-web-push/admin-web-push.component';
import { LimitWarningComponent } from './limit-warning/limit-warning.component';
import { SunsetBannerComponent } from './sunset-banner/sunset-banner.component';
import { AdminBannerComponent } from './admin/admin-banner/admin-banner.component';

// socket-io init
import { environment } from '../environments/environment';
import { SocketIoModule, SocketIoConfig } from 'ngx-socket-io';
import { SocketService } from './services/socket.service';
import { AdminMapComponent } from './admin/admin-map/admin-map.component';
import { ServiceWorkerService } from './services/sw.service';
import { AggregateContactsService } from './services/aggregate-contacts.service';
import { EmailCampaignService } from './services/email-campaign.service';
import { DriftBotInboxService } from './services/drift-bot-inbox.service';
import { EmailSummarizeComponent } from './inbox/email-summarize/email-summarize.component';

const socketConfig: SocketIoConfig = { url: environment.socketIo, options: { transports: ['websocket', 'polling'] } };

export function initApp(initService: InitService) {
  return () => initService.boot();
}

@NgModule({
  declarations: [
    AppComponent,
    HomeComponent,
    DashboardComponent,
    SuspendedComponent,
    LoginComponent,
    RegisterComponent,
    SettingsComponent,
    LogoutComponent,
    LoadingComponent,
    AuthLevelDirective,
    AuthLevelMaskDirective,
    DriftNavComponent,
    SettingsNavComponent,
    SettingsNavDropdownComponent,
    GlobalMessageComponent,
    SettingsDomainsComponent,
    GlobalErrorComponent,
    ConfirmDialogComponent,
    SettingsIntegrationComponent,
    AlertDialogComponent,
    SettingsContactComponent,
    SettingsUsersComponent,
    AccountDialogComponent,
    ContactsComponent,
    FilterPipe,
    CsvExportComponent,
    InboxComponentOld,
    EmailDetailComponentOLD,
    WorkflowsComponent,
    WfDescriptionComponent,
    WFDetailComponent,
    AlertWfDetailComponent,
    WFActonDetailComponent,
    SettingsSubscriptionComponent,
    PasswordResetComponent,
    DemoComponent,
    PreTrialComponent,
    AdminComponent,
    AdminNavComponent,
    AdminAccountComponent,
    WorkflowHistoryComponent,
    AdminEnrichComponent,
    AdminWorkflowComponent,
    SettingsInboxesComponent,
    InboxesAuthComponent,
    GoogleLoginComponent,
    AdminNbClassifierComponent,
    NbClassifyOutputComponent,
    AdminEmailDetailComponent,
    AdminNerComponent,
    NerOutputComponent,
    AdminParserComponent,
    AdminFilterComponent,
    AdminSubjectClassifierComponent,
    AdminSystemComponent,
    WfDetailPardotComponent,
    AdminUtilitiesComponent,
    EnrichComponent,
    EnrichGraphComponent,
    AdminSpamClassifierComponent,
    SpamBodyOutputComponent,
    ConfigComponent,
    ConfigNavComponent,
    HumanGraphComponent,
    DateRangePickerComponent,
    AdminCleanupComponent,
    AdminUnsubClassifierComponent,
    AdminHeaderClassifierComponent,
    CdoComponent,
    CdoDetailComponent,
    CdoDescriptionComponent,
    EmailTypePickerComponent,
    RouteTrackComponent,
    RouteTrackDetailComponent,
    SourcePickerComponent,
    LanguagePickerComponent,
    AdminJobComponent,
    PartnerPickerComponent,
    WizardStepsComponent,
    PartnerMapComponent,
    PartnerListPickerComponent,
    PartnerStaticfieldPickerComponent,
    PartnerSiftrockfieldPickerComponent,
    PartnerLeadownerPickerComponent,
    FieldFilterComponent,
    AdminFailedTasksComponent,
    SourceFilterComponent,
    GlobalUpdateComponent,
    AdminJobsComponent,
    NewUserComponent,
    AdminTypeOverrrideComponent,
    EmailFilterGroupComponent,
    SettingsBotComponent,
    BotAuthComponent,
    AutomateComponent,
    SkillListComponent,
    AdminClassifierComponent,
    SkillDetailComponent,
    TaskDirective,
    RouteTaskComponent,
    RespondTaskComponent,
    RespondOtterTaskComponent,
    TrackTaskComponent,
    RespondtrackTaskComponent,
    CreateTaskComponent,
    ConfidencePickerComponent,
    InboxComponent,
    EmailDetailComponent,
    TaskHistoryComponent,
    PartnerSenderfieldPickerComponent,
    TaskPopoverComponent,
    FilterPopoverComponent,
    SettingsPrivacyComponent,
    BotDraftComponent,
    AdminInboxComponent,
    AdminCacheComponent,
    AdminGoldensetComponent,
    GotoComponent,
    AdminMapComponent,
    AdminWebPushComponent,
    LimitWarningComponent,
    SunsetBannerComponent,
    AdminBannerComponent,
    EmailSummarizeComponent
  ],
  entryComponents: [
    ConfirmDialogComponent,
    AlertDialogComponent,
    AccountDialogComponent,
    RespondTaskComponent,
    RespondOtterTaskComponent,
    RespondtrackTaskComponent,
    RouteTaskComponent,
    TrackTaskComponent,
    CreateTaskComponent,
    EmailFilterGroupComponent
  ],
  imports: [
    BrowserModule,
    EditorModule,
    BrowserAnimationsModule,
    FormsModule,
    ReactiveFormsModule,
    HttpModule,
    HttpClientModule,
    ClipboardModule,
    RouterModule.forRoot(routerConfig, { onSameUrlNavigation: 'reload' }),
    ModalModule.forRoot(),
    BsDropdownModule.forRoot(),
    PopoverModule.forRoot(),
    AccordionModule.forRoot(),
    TooltipModule.forRoot(),
    MomentModule,
    ChartModule,
    SelectModule,
    MultiSelectModule,
    PrettyJsonModule,
    SocketIoModule.forRoot(socketConfig)
  ],
  providers: [
    // { provide: Http, useClass: HttpService },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true},
    { provide: APP_INITIALIZER, useFactory: initApp, deps: [InitService], multi: true},
    { provide: ErrorHandler, useClass: GlobalErrorHandler},
    HttpClientModule,
    SessionService,
    DecimalPipe,
    Constants,
    AuthService,
    CacheService,
    InitService,
    LogService,
    SentryService,
    ErrorService,
    ConfigService,
    PartnerService,
    MarketoService,
    EloquaService,
    HubspotService,
    PardotService,
    ActonService,
    AccountService,
    AggregateContactsService,
    SkuService,
    UserService,
    AggregateService,
    ContactService,
    ContactIgnoreService,
    EmailDetailService,
    WorkflowService,
    EnrichService,
    AutomateJobService,
    AutomateTaskHistoryService,
    ApproveQService,
    FilterService,
    ClassifierService,
    NerService,
    MatchesService,
    ParserService,
    OpsService,
    ChartService,
    VersionService,
    SourceService,
    GlobalMessageService,
    UtilService,
    EventService,
    DriftService,
    GmailService,
    O365Service,
    AdminService,
    AdminHeaderClassifierService,
    AdminNBClassifierService,
    AdminRegexClassifierService,
    AdminSpamClassifierService,
    AdminOpsService,
    AdminOpsInternalService,
    AdminSubjectClassifierService,
    AdminClassifierService,
    AdminCacheService,
    AdminGoldensetResultService,
    AuthGuard,
    JwtHelper,
    Animations,
    EloquaCdoService,
    SkillService,
    ShortUrlService,
    SocketService,
    TypeOverrideService,
    ServiceWorkerService,
    EmailCampaignService,
    DriftBotInboxService,
    CookieService,
    {
      provide: HighchartsStatic,
      useFactory: highchartsFactory
    }
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
  constructor(private sessionService: SessionService) {
    sessionService.initSession();
  }
}



// WEBPACK FOOTER //
// ./client/app/app.module.ts